import { Component } from '@theme/component';

/**
 * @typedef {Object} RecommendationsSliderRefs
 * @property {HTMLElement} viewport - The scrollable viewport
 * @property {HTMLElement} track - The flex track holding the slides
 * @property {HTMLButtonElement} [prevButton] - Previous-slide button
 * @property {HTMLButtonElement} [nextButton] - Next-slide button
 * @property {HTMLButtonElement} [playPauseButton] - Autoplay pause/play toggle
 * @property {HTMLElement} [pauseIcon] - Pause icon shown while rotating
 * @property {HTMLElement} [playIcon] - Play icon shown while paused
 */

/**
 * @extends {Component<RecommendationsSliderRefs>}
 */
class RecommendationsSlider extends Component {
  requiredRefs = ['viewport', 'track'];

  #autoplayInterval = null;
  #scrollEndTimeout = null;
  #isJumping = false;
  #isPaused = false;
  #userPaused = false;
  #visibilityObserver = null;
  #slideCount = 0;
  #loopEnabled = false;
  #autoplayEnabled = false;
  #autoplaySpeed = 5000;
  #prefersReducedMotion = false;
  #boundHandlers = {};
  #resizeObserver = null;
  /** What Liquid asked for, kept separate from what's currently active so compact mode is reversible. */
  #autoplayRequested = false;
  #compactQuery = null;
  #onCompactChange = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('data-recommendations-slider-ready')) return;

    this.setAttribute('data-recommendations-slider-ready', '');

    this.#loopEnabled = this.dataset.loop === 'true';
    this.#autoplayRequested = this.dataset.autoplay === 'true';
    this.#autoplayEnabled = this.#autoplayRequested;
    this.#autoplaySpeed = Number(this.dataset.autoplaySpeed) || 5000;
    this.#prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.#prefersReducedMotion) {
      this.#autoplayEnabled = false;
    }

    this.#slideCount = this.#getSlides().length;

    if (this.#slideCount <= 1) {
      this.dataset.singleSlide = 'true';
      this.#loopEnabled = false;
    }

    // Compact mode: below 750px, no autoplay and no controls (swipe only). One condition
    // owns both — hiding the pause button while autoplay ran would breach WCAG 2.2.2, so
    // they must not be able to diverge. CSS hides the controls off data-compact.
    if (this.dataset.mobileCompact === 'true') {
      this.#compactQuery = window.matchMedia('(max-width: 749px)');
      this.#onCompactChange = () => this.#applyCompact();
      this.#compactQuery.addEventListener('change', this.#onCompactChange);
      this.#applyCompact();
    }

    this.#updateSlideWidth();
    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateSlideWidth();
      this.#updateControls();
    });
    this.#resizeObserver.observe(this);

    this.#bindEvents();
    this.#updateControls();

    if (this.#autoplayEnabled && this.#slideCount > 1) {
      this.#startAutoplay();
      this.#observeVisibility();
    }

    this.#syncAriaLive();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeAttribute('data-recommendations-slider-ready');
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#pauseAutoplay();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    // The cart drawer re-injects this section on every cart update, so an un-removed
    // matchMedia listener would accumulate one leak per add-to-cart.
    if (this.#onCompactChange) {
      this.#compactQuery?.removeEventListener('change', this.#onCompactChange);
      this.#onCompactChange = null;
      this.#compactQuery = null;
    }
    clearTimeout(this.#scrollEndTimeout);

    if (this.#boundHandlers.scroll) {
      this.refs.viewport?.removeEventListener('scroll', this.#boundHandlers.scroll);
    }

    if ('onscrollend' in window && this.#boundHandlers.scrollend) {
      this.refs.viewport?.removeEventListener('scrollend', this.#boundHandlers.scrollend);
    }

    for (const eventName of ['mouseenter', 'mouseleave', 'focusin', 'focusout']) {
      this.removeEventListener(eventName, this.#boundHandlers[eventName]);
    }
  }

  /**
   * @param {Event} event
   */
  handlePrevClick(event) {
    this.#pauseAutoplayTemporarily();
    this.#scrollPrev();
  }

  /**
   * @param {Event} event
   */
  handleNextClick(event) {
    this.#pauseAutoplayTemporarily();
    this.#scrollNext();
  }

  /**
   * @param {Event} event
   */
  handlePlayPauseClick(event) {
    this.#togglePlayPause();
  }

  /**
   * @param {Event} event
   */
  handlePointerDown(event) {
    this.#pauseAutoplayTemporarily();
  }

  #parseSlideWidth(value) {
    const raw = (value || '75%').trim();

    if (raw.endsWith('%')) {
      return { type: 'ratio', value: parseFloat(raw) / 100 };
    }

    if (raw.endsWith('px')) {
      return { type: 'fixed', value: parseFloat(raw) };
    }

    const numeric = parseFloat(raw);
    if (Number.isNaN(numeric)) {
      return { type: 'ratio', value: 0.75 };
    }

    return numeric <= 1 ? { type: 'ratio', value: numeric } : { type: 'fixed', value: numeric };
  }

  #updateSlideWidth() {
    const config = this.#parseSlideWidth(this.dataset.slideWidth);
    const sliderWidth = this.clientWidth;

    if (!sliderWidth) return;

    let slideWidth = config.type === 'fixed' ? config.value : sliderWidth * config.value;

    const maxFromData = Number(this.dataset.slideWidthMax);
    const maxFromCss = parseFloat(getComputedStyle(this).getPropertyValue('--recommendations-slider-slide-max'));
    const maxWidth = !Number.isNaN(maxFromData) && maxFromData > 0 ? maxFromData : maxFromCss;

    if (!Number.isNaN(maxWidth) && maxWidth > 0) {
      slideWidth = Math.min(slideWidth, maxWidth);
    }

    this.style.setProperty('--recommendations-slider-slide-width', `${Math.round(slideWidth)}px`);
  }

  #getSlides() {
    return Array.from(this.refs.track.querySelectorAll('.recommendations-slider__slide'));
  }

  #hasScrollOverflow() {
    return this.refs.viewport.scrollWidth > this.refs.viewport.clientWidth + 1;
  }

  #isAtScrollEnd() {
    if (!this.#hasScrollOverflow()) return false;

    const { scrollLeft, scrollWidth, clientWidth } = this.refs.viewport;
    return scrollLeft >= scrollWidth - clientWidth - 2;
  }

  #getCurrentSlideIndex() {
    const slides = this.#getSlides();
    if (!slides.length) return 0;

    const scrollLeft = this.refs.viewport.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  #getLastSlideIndex() {
    return Math.max(0, this.#slideCount - 1);
  }

  #scrollToSlide(index, animate = true) {
    const slides = this.#getSlides();
    const slide = slides[index];
    if (!slide) return;

    const targetScrollLeft = slide.offsetLeft;
    const useAnimation = animate && !this.#prefersReducedMotion;

    this.#isJumping = true;
    this.refs.viewport.style.scrollSnapType = 'none';

    if (useAnimation) {
      this.refs.viewport.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });
    } else {
      this.refs.viewport.scrollLeft = targetScrollLeft;
    }

    const finishScroll = () => {
      this.refs.viewport.style.removeProperty('scroll-snap-type');
      this.#isJumping = false;
      this.#updateControls();
    };

    if (useAnimation) {
      if ('onscrollend' in window) {
        this.refs.viewport.addEventListener('scrollend', finishScroll, { once: true });
      } else {
        setTimeout(finishScroll, 350);
      }
    } else {
      requestAnimationFrame(finishScroll);
    }
  }

  #shouldLoopForward() {
    if (!this.#loopEnabled) return false;

    const currentIndex = this.#getCurrentSlideIndex();
    return currentIndex >= this.#getLastSlideIndex() || this.#isAtScrollEnd();
  }

  #shouldLoopBackward() {
    if (!this.#loopEnabled) return false;
    return this.#getCurrentSlideIndex() <= 0;
  }

  #scrollNext() {
    const currentIndex = this.#getCurrentSlideIndex();

    if (this.#shouldLoopForward()) {
      this.#scrollToSlide(0, false);
      return;
    }

    if (currentIndex >= this.#getLastSlideIndex()) return;

    this.#scrollToSlide(currentIndex + 1);
  }

  #scrollPrev() {
    const currentIndex = this.#getCurrentSlideIndex();

    if (this.#shouldLoopBackward()) {
      this.#scrollToSlide(this.#getLastSlideIndex(), false);
      return;
    }

    this.#scrollToSlide(currentIndex - 1);
  }

  #handleScrollEnd() {
    if (this.#isJumping) return;
    this.#updateControls();
  }

  #updateControls() {
    if (!this.refs.prevButton || !this.refs.nextButton) return;

    if (this.#slideCount <= 1) {
      this.refs.prevButton.disabled = true;
      this.refs.nextButton.disabled = true;
      return;
    }

    if (this.#loopEnabled) {
      this.refs.prevButton.disabled = false;
      this.refs.nextButton.disabled = false;
      return;
    }

    const currentIndex = this.#getCurrentSlideIndex();

    this.refs.prevButton.disabled = currentIndex <= 0;
    this.refs.nextButton.disabled = currentIndex >= this.#getLastSlideIndex();
  }

  /**
   * Compact mode (opt-in via data-mobile-compact): below 750px there is no autoplay and
   * no controls — swipe, or Tab through the cards. Autoplay and the controls are decided
   * here together on purpose: hiding the pause button while autoplay still ran would
   * breach WCAG 2.2.2, so they must not be able to diverge. CSS hides the controls off
   * data-compact, which only this method writes.
   *
   * Reversible, hence #autoplayRequested: matchMedia fires on resize (and on a desktop
   * browser you can cross the breakpoint in both directions), so compact mode must be
   * able to hand autoplay back rather than disabling it permanently.
   */
  #applyCompact() {
    const compact = this.#compactQuery?.matches ?? false;
    this.dataset.compact = compact ? 'true' : 'false';

    const shouldAutoplay =
      this.#autoplayRequested && !this.#prefersReducedMotion && !compact && this.#slideCount > 1;

    // No transition — bail before touching the interval or the visibility observer, so
    // the initial non-compact call doesn't double up with connectedCallback's own start.
    if (shouldAutoplay === this.#autoplayEnabled) return;

    this.#autoplayEnabled = shouldAutoplay;

    if (shouldAutoplay) {
      this.#startAutoplay();
      this.#observeVisibility();
    } else {
      this.#pauseAutoplay();
    }

    this.#syncAriaLive();
  }

  #startAutoplay() {
    this.#pauseAutoplay();

    this.#autoplayInterval = setInterval(() => {
      if (this.#isPaused || this.#userPaused || document.hidden) return;
      this.#scrollNext();
    }, this.#autoplaySpeed);
  }

  #pauseAutoplay() {
    if (this.#autoplayInterval) {
      clearInterval(this.#autoplayInterval);
      this.#autoplayInterval = null;
    }
  }

  #observeVisibility() {
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        this.#isPaused = !entries[0].isIntersecting;
        this.#syncAriaLive();
      },
      { threshold: 0.1 },
    );

    this.#visibilityObserver.observe(this);
  }

  /**
   * Whether slides are actively auto-advancing right now, accounting for
   * manual pause, temporary hover/focus/visibility pause, and reduced motion.
   */
  #isRotating() {
    return this.#autoplayEnabled && this.#slideCount > 1 && !this.#userPaused && !this.#isPaused && !document.hidden;
  }

  /**
   * Keep the viewport's aria-live region in sync with actual rotation state:
   * 'off' while auto-advancing (avoids announcement overload), 'polite' whenever
   * paused so screen readers announce the settled slide.
   */
  #syncAriaLive() {
    if (!this.#autoplayEnabled) return;

    this.refs.viewport?.setAttribute('aria-live', this.#isRotating() ? 'off' : 'polite');
  }

  #togglePlayPause() {
    this.#userPaused = !this.#userPaused;

    if (this.refs.playPauseButton) {
      this.refs.playPauseButton.setAttribute('aria-pressed', String(this.#userPaused));
      this.refs.playPauseButton.setAttribute(
        'aria-label',
        this.#userPaused ? 'Start slide rotation' : 'Pause slide rotation',
      );
    }

    this.refs.pauseIcon?.toggleAttribute('hidden', this.#userPaused);
    this.refs.playIcon?.toggleAttribute('hidden', !this.#userPaused);

    this.#syncAriaLive();
  }

  #bindEvents() {
    this.#boundHandlers.scroll = () => {
      clearTimeout(this.#scrollEndTimeout);
      this.#scrollEndTimeout = setTimeout(() => this.#handleScrollEnd(), 100);
    };

    this.#boundHandlers.scrollend = () => {
      clearTimeout(this.#scrollEndTimeout);
      this.#handleScrollEnd();
    };

    this.#boundHandlers.mouseenter = () => {
      this.#isPaused = true;
      this.#syncAriaLive();
    };

    this.#boundHandlers.mouseleave = () => {
      this.#isPaused = false;
      this.#syncAriaLive();
    };

    this.#boundHandlers.focusin = () => {
      this.#isPaused = true;
      this.#syncAriaLive();
    };

    this.#boundHandlers.focusout = (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.#isPaused = false;
        this.#syncAriaLive();
      }
    };

    this.refs.viewport.addEventListener('scroll', this.#boundHandlers.scroll, { passive: true });

    if ('onscrollend' in window) {
      this.refs.viewport.addEventListener('scrollend', this.#boundHandlers.scrollend);
    }

    this.addEventListener('mouseenter', this.#boundHandlers.mouseenter);
    this.addEventListener('mouseleave', this.#boundHandlers.mouseleave);
    this.addEventListener('focusin', this.#boundHandlers.focusin);
    this.addEventListener('focusout', this.#boundHandlers.focusout);
  }

  #pauseAutoplayTemporarily() {
    this.#isPaused = true;
    this.#syncAriaLive();

    setTimeout(() => {
      this.#isPaused = false;
      this.#syncAriaLive();
    }, this.#autoplaySpeed * 2);
  }
}

if (!customElements.get('recommendations-slider')) {
  customElements.define('recommendations-slider', RecommendationsSlider);
}
